// src/pages/LabelledStockList.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaPrint,
  FaImage,
  FaSearch,
  FaList,
  FaThLarge,
} from "react-icons/fa";
import { FiRotateCcw, FiMoreVertical } from "react-icons/fi";
import { TbArrowsSort } from "react-icons/tb";
import Select from "react-select";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  useGetAllLabelledStockQuery,
  useDeleteLabelledItemMutation,
  useBulkDeleteLabelledMutation,
  useVisualSearchLabelledItemsMutation,
} from "../toolkitServices/stockApi";

// masters
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetAllVendorsQuery } from "../toolkitServices/vendorApi";
import { useGetAllLabelTemplatesQuery } from "../toolkitServices/labelTemplateApi";
import { useGetCountersAllQuery } from "../toolkitServices/counterApi"; // ✅ counters

import { useDispatch, useSelector } from "react-redux";
import { setLabelledList } from "../slices/labelledStockSlice";
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setVendorList } from "../slices/vendorSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";

import { handleDropDowns } from "../config/dropDownHelpers";
import { fmtDate } from "../utils/date";
import { generateLabelsPdfForStock } from "../utils/labelPdfHelperStock";
import { fillPrnTemplate } from "../utils/fillPrnTemplate";
import { downloadPrnFile } from "../utils/downloadPrnFile";
/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ----------------- helpers ----------------- */
const toTitle = (s = "") => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// react-select styling
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #7c3aed33" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const headerCell =
  "px-1 sm:px-2 py-1 text-[7px] sm:text-[9px] font-semibold uppercase tracking-wide";
const dataCell = "px-1 sm:px-2 py-1 text-xs sm:text-xs";

/* ------------- skeleton rows (table) ------------- */
const SkeletonRows = ({ cols = 12, rows = 8, actionColumn = false }) => {
  const widths = ["w-10", "w-20", "w-28", "w-16", "w-24", "w-32"]; // cycle

  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={`sk-${r}`} className="animate-pulse">
          {Array.from({ length: cols }).map((__, c) => (
            <td
              key={`sk-${r}-${c}`}
              className={
                actionColumn && c === cols - 1
                  ? `${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] border-l border-violet-100 bg-violet-50 sm:w-[104px] sm:min-w-[104px]`
                  : dataCell
              }
            >
              <div
                className={`h-3 ${widths[c % widths.length]} bg-slate-200 rounded`}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

/* ------------- skeleton cards (grid) ------------- */
const GridSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={`sk-card-${i}`}
        className="border rounded-2xl bg-white shadow-sm overflow-hidden animate-pulse"
      >
        <div className="aspect-[4/3] bg-slate-200" />
        <div className="p-3 space-y-2">
          <div className="h-4 w-24 bg-slate-200 rounded" />
          <div className="h-3 w-32 bg-slate-200 rounded" />
          <div className="h-3 w-20 bg-slate-200 rounded" />
        </div>
      </div>
    ))}
  </div>
);

/* ------------- Image Slider per card ------------- */
const ImageCarousel = ({ images = [] }) => {
  const [index, setIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="flex items-center justify-center w-full aspect-[4/3] bg-slate-100 text-slate-400 text-xs">
        No image
      </div>
    );
  }

  const total = images.length;
  const current = images[index];

  const go = (dir) => {
    setIndex((prev) => {
      if (dir === "prev") return prev === 0 ? total - 1 : prev - 1;
      return prev === total - 1 ? 0 : prev + 1;
    });
  };

  return (
    <div className="relative w-full aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden">
      <img
        src={current.url}
        alt={current.originalName || "Item image"}
        className="w-full h-full object-cover"
      />

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => go("prev")}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full h-7 w-7 flex items-center justify-center text-xs"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => go("next")}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full h-7 w-7 flex items-center justify-center text-xs"
          >
            ›
          </button>

          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
            {images.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${
                  i === index ? "bg-white" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// 🖼 helper: remote image → dataURL for jsPDF
const loadImageAsDataUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      resolve(dataUrl);
    };

    img.onerror = (err) => {
      console.error("Image load failed", err);
      resolve(null);
    };

    img.src = url;
  });

/* ---------------- LABEL TEMPLATE PICKER ---------------- */
const pickTemplateForItem = (templates, item) => {
  if (!Array.isArray(templates) || !templates.length) return null;

  const rawBranch =
    item?.branchId ??
    item?.BranchId ??
    item?.branch?._id ??
    item?.branch?.value ??
    null;

  const rawCategory =
    item?.categoryId ??
    item?.CategoryId ??
    item?.category?._id ??
    item?.category?.value ??
    null;

  const b = rawBranch != null ? String(rawBranch) : null;
  const c = rawCategory != null ? String(rawCategory) : null;

  const inList = (arr, value) =>
    !!value &&
    Array.isArray(arr) &&
    arr.map(String).some((x) => x === String(value));

  let best = null;
  let bestScore = -1;

  for (const t of templates) {
    const layout = t.layout || t;

    const allB = !!layout.appliesToAllBranches;
    const allC = !!layout.appliesToAllCategories;
    const hasB = inList(layout.branchIds, b);
    const hasC = inList(layout.categoryIds, c);

    const branchMatches = b ? allB || hasB : true;
    const catMatches = c ? allC || hasC : true;

    if (!branchMatches || !catMatches) continue;

    let score = 0;
    if (hasB) score += 2;
    else if (allB) score += 1;
    if (hasC) score += 2;
    else if (allC) score += 1;
    if (layout.isDefault || t.isDefault) score += 0.5;

    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  }

  if (best) return best;

  const def =
    templates.find((t) => t.isDefault || t.layout?.isDefault) || templates[0];
  return def || null;
};

/* ---------------------------- MAIN COMPONENT ---------------------------- */
const canDelete = (user) => {
  console.log(user);
  const perm = user?.permissions?.find((p) => p.moduleKey === "stocks");
  return user?.role === "owner" || perm?.permissions?.delete;
};

const LabelledStockList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  // 🔐 client user
  const authUser = useSelector((s) => s.auth?.user);
  console.log(authUser);
  console.log(user);
  // header controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [aiSearchOpen, setAiSearchOpen] = useState(false);
  const [aiSearchFile, setAiSearchFile] = useState(null);
  const [aiSearchPreview, setAiSearchPreview] = useState("");
  const [aiSearchResults, setAiSearchResults] = useState([]);
  const [aiSearchActive, setAiSearchActive] = useState(false);
  const [aiSearchLimit, setAiSearchLimit] = useState(20);
  const [visualSearchLabelledItems, { isLoading: searchingAi }] =
    useVisualSearchLabelledItemsMutation();
  // sorting
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // list / grid view
  const [viewMode, setViewMode] = useState("table");

  // filters (draft vs applied)
  const emptyFilter = {
    vendorId: "",
    branchId: "",
    counterId: "", // ✅ counter
    categoryId: "",
    productId: "",
    designId: "",
    purityId: "",
    dateFrom: "",
    dateTo: "",
  };
  const [appliedFilters, setAppliedFilters] = useState(emptyFilter);
  const [draftFilters, setDraftFilters] = useState(emptyFilter);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const vendorSelectRef = useRef(null);

  // selections across searches/pages
  const [selectedItems, setSelectedItems] = useState(new Map());

  // bulk menu
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);

  // confirmation dialog state
  const [confirm, setConfirm] = useState({
    open: false,
    mode: null,
    payload: null,
  });

  // WhatsApp share modal
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePhone, setSharePhone] = useState(""); // kept
  const [shareNote, setShareNote] = useState("Hi, please check these designs.");

  // Catalog preview
  const [catalogPreviewUrl, setCatalogPreviewUrl] = useState(null);
  const [catalogPreviewOpen, setCatalogPreviewOpen] = useState(false);

  const closeCatalogPreview = () => {
    if (catalogPreviewUrl) URL.revokeObjectURL(catalogPreviewUrl);
    setCatalogPreviewUrl(null);
    setCatalogPreviewOpen(false);
  };

  const handleDownloadCatalogFromPreview = () => {
    if (!catalogPreviewUrl) return;
    const link = document.createElement("a");
    link.href = catalogPreviewUrl;
    link.download = "premium-jewellery-brochure.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Catalog PDF download started.");
  };

  // masters
  const { data: vendors } = useGetAllVendorsQuery();
  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products } = useGetAllProductsQuery();
  const { data: designs } = useGetAllDesignsQuery();
  const { data: purities } = useGetAllPuritiesQuery();
  const { data: counters } = useGetCountersAllQuery(); // ✅ counters list
  const { data: labelTemplatesRes } = useGetAllLabelTemplatesQuery(
    authUser?.clientCode ? { clientCode: authUser.clientCode } : {},
  );

  useEffect(() => {
    return () => {
      if (aiSearchPreview) URL.revokeObjectURL(aiSearchPreview);
    };
  }, [aiSearchPreview]);

  const labelTemplates =
    labelTemplatesRes?.data || labelTemplatesRes?.items || [];

  // push masters to store
  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories.data));
    if (products?.data?.length) dispatch(setProductList(products.data));
    if (vendors?.data?.length) dispatch(setVendorList(vendors.data));
    if (designs?.data?.length) dispatch(setDesignList(designs.data));
    if (purities?.data?.length) dispatch(setPurityList(purities.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
  }, [dispatch, categories, products, vendors, designs, purities, branches]);

  // state selectors
  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const vendorsFromState = useSelector((s) => s.vendors?.vendors);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);

  // query (applied filters only)
  const { data, isFetching, isSuccess } = useGetAllLabelledStockQuery({
    page,
    limit,
    search,
    status: statusFilter,
    sortBy,
    sortOrder,
    ...appliedFilters, // ✅ includes counterId
  });

  const rowsFromStore =
    useSelector((state) => state?.labelledStocks?.labelledStock) || [];

  useEffect(() => {
    if (isSuccess && Array.isArray(data?.data)) {
      dispatch(setLabelledList(data.data));
    }
  }, [isSuccess, data, dispatch]);

  // keyboard: Ctrl+P to create
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/stocks/labelled-stock-list/create-labelled-stock");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // debounce => page 1
  useEffect(() => {
    const t = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(t);
  }, [search, statusFilter, limit, appliedFilters, sortBy, sortOrder]);

  // pagination meta
  const totalDocs = data?.pagination?.totalDocs ?? data?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? data?.pages ?? 1;
  const hasPrev = data?.pagination?.hasPrev ?? page > 1;
  const hasNext = data?.pagination?.hasNext ?? page < totalPages;

  // deletable helper
  const isDeletable = (it) =>
    String(it?.status) === "active" && it?.isActive !== false;
  const isSameDayDelete = (createdAt) => {
    if (!createdAt) return true;

    const itemDate = new Date(createdAt).toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata",
    });

    const today = new Date().toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata",
    });

    return itemDate === today;
  };
  // ---- dropdown options ----
  const findOpt = (opts, v) =>
    (opts || []).find((o) => String(o.value) === String(v)) || null;

  const vendorOptions = useMemo(
    () => handleDropDowns({ key: "vendor", vendors: vendorsFromState }),
    [vendorsFromState],
  );
  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branchesFromState }),
    [branchesFromState],
  );
  const categoryOptions = useMemo(
    () => handleDropDowns({ key: "category", categories: categoriesFromState }),
    [categoriesFromState],
  );

  const vendorValue = useMemo(
    () => findOpt(vendorOptions, draftFilters.vendorId),
    [vendorOptions, draftFilters.vendorId],
  );
  const branchValue = useMemo(
    () => findOpt(branchOptions, draftFilters.branchId),
    [branchOptions, draftFilters.branchId],
  );
  const categoryValue = useMemo(
    () => findOpt(categoryOptions, draftFilters.categoryId),
    [categoryOptions, draftFilters.categoryId],
  );

  // ✅ Counters: branch-dependent
  const countersRaw = counters?.data || counters?.items || counters || [];

  const getCounterId = (c) =>
    c?._id ?? c?.id ?? c?.Id ?? c?.counterId ?? c?.CounterId ?? "";

  const getBranchId = (c) =>
    c?.branchId ??
    c?.BranchId ??
    c?.branch?._id ??
    c?.branch?.id ??
    c?.Branch?._id ??
    c?.Branch?.id ??
    "";

  const getCounterName = (c) =>
    c?.counterName ??
    c?.CounterName ??
    c?.name ??
    c?.Name ??
    c?.counterTitle ??
    c?.title ??
    "Counter";

  // factory: branchId passed => options filtered
  const buildCounterOptions = (branchId) => {
    const bId = branchId ? String(branchId) : "";
    const safe = Array.isArray(countersRaw) ? countersRaw : [];

    const list = safe
      .filter((c) => {
        if (!bId) return true; // ✅ branch not selected -> show ALL counters
        const cb = getBranchId(c);
        return cb != null && String(cb) === bId; // ✅ branch selected -> ONLY that branch counters
      })
      .map((c) => ({
        value: String(getCounterId(c)),
        label: getCounterName(c),
      }))
      .filter((o) => o.value && o.value !== "undefined" && o.value !== "null");

    // remove duplicates
    const map = new Map();
    for (const o of list) map.set(o.value, o);
    return Array.from(map.values());
  };

  // ✅ Draft modal counters (depends on draft branch)
  const counterOptionsDraft = useMemo(
    () => buildCounterOptions(draftFilters.branchId),
    [countersRaw, draftFilters.branchId],
  );

  const counterValue = useMemo(
    () => findOpt(counterOptionsDraft, draftFilters.counterId),
    [counterOptionsDraft, draftFilters.counterId],
  );

  // ✅ Chips label counters (depends on applied branch) — IMPORTANT FIX
  const counterOptionsApplied = useMemo(
    () => buildCounterOptions(appliedFilters.branchId),
    [countersRaw, appliedFilters.branchId],
  );

  const productOptions = useMemo(
    () =>
      handleDropDowns({
        key: "product",
        products: productsFromState,
        category: categoryValue,
      }),
    [productsFromState, categoryValue],
  );
  const productValue = useMemo(
    () => findOpt(productOptions, draftFilters.productId),
    [productOptions, draftFilters.productId],
  );

  const designOptions = useMemo(
    () =>
      handleDropDowns({
        key: "design",
        designs: designFromState,
        product: productValue,
      }),
    [designFromState, productValue],
  );
  const designValue = useMemo(
    () => findOpt(designOptions, draftFilters.designId),
    [designOptions, draftFilters.designId],
  );

  const purityOptions = useMemo(
    () =>
      handleDropDowns({
        key: "purity",
        purities: purityFromState,
        category: categoryValue,
      }),
    [purityFromState, categoryValue],
  );
  const purityValue = useMemo(
    () => findOpt(purityOptions, draftFilters.purityId),
    [purityOptions, draftFilters.purityId],
  );

  // handlers (draft)
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters((s) => ({ ...s, [name]: value }));
  };

  const onVendorChange = (opt) =>
    setDraftFilters((s) => ({ ...s, vendorId: opt?.value || "" }));

  // ✅ branch change resets counter always
  const onBranchChange = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      branchId: opt?.value || "",
      counterId: "", // ✅ reset counter when branch changes
    }));

  const onCounterChange = (opt) =>
    setDraftFilters((s) => ({ ...s, counterId: opt?.value || "" }));

  const onCategoryChange = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      categoryId: opt?.value || "",
      productId: "",
      designId: "",
      purityId: "",
    }));
  const onProductChange = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      productId: opt?.value || "",
      designId: "",
    }));
  const onDesignChange = (opt) =>
    setDraftFilters((s) => ({ ...s, designId: opt?.value || "" }));
  const onPurityChange = (opt) =>
    setDraftFilters((s) => ({ ...s, purityId: opt?.value || "" }));

  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setIsFilterOpen(true);
  };
  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setIsFilterOpen(false);
  };
  const resetDraftFilters = () => setDraftFilters(emptyFilter);

  // chips: labels for applied filters
  const labelFromOpts = (opts, v) => findOpt(opts, v)?.label || "";
  const chips = useMemo(() => {
    const list = [];
    if (statusFilter)
      list.push({ key: "status", label: `Status: ${toTitle(statusFilter)}` });
    if (appliedFilters.vendorId)
      list.push({
        key: "vendorId",
        label: `Vendor: ${labelFromOpts(
          vendorOptions,
          appliedFilters.vendorId,
        )}`,
      });
    if (appliedFilters.branchId)
      list.push({
        key: "branchId",
        label: `Branch: ${labelFromOpts(
          branchOptions,
          appliedFilters.branchId,
        )}`,
      });

    // ✅ counter chip MUST use applied-branch counter list
    if (appliedFilters.counterId)
      list.push({
        key: "counterId",
        label: `Counter: ${labelFromOpts(
          counterOptionsApplied,
          appliedFilters.counterId,
        )}`,
      });

    if (appliedFilters.categoryId)
      list.push({
        key: "categoryId",
        label: `Category: ${labelFromOpts(
          categoryOptions,
          appliedFilters.categoryId,
        )}`,
      });
    if (appliedFilters.productId)
      list.push({
        key: "productId",
        label: `Product: ${labelFromOpts(
          productOptions,
          appliedFilters.productId,
        )}`,
      });
    if (appliedFilters.designId)
      list.push({
        key: "designId",
        label: `Design: ${labelFromOpts(
          designOptions,
          appliedFilters.designId,
        )}`,
      });
    if (appliedFilters.purityId)
      list.push({
        key: "purityId",
        label: `Purity: ${labelFromOpts(
          purityOptions,
          appliedFilters.purityId,
        )}`,
      });
    if (appliedFilters.dateFrom)
      list.push({ key: "dateFrom", label: `From: ${appliedFilters.dateFrom}` });
    if (appliedFilters.dateTo)
      list.push({ key: "dateTo", label: `To: ${appliedFilters.dateTo}` });
    if (search) list.push({ key: "search", label: `Search: ${search}` });
    return list;
  }, [
    appliedFilters,
    statusFilter,
    search,
    vendorOptions,
    branchOptions,
    counterOptionsApplied,
    categoryOptions,
    productOptions,
    designOptions,
    purityOptions,
  ]);

  const clearChip = (key) => {
    if (key === "status") setStatusFilter("");
    else if (key === "search") setSearch("");
    else setAppliedFilters((s) => ({ ...s, [key]: "" }));
    setPage(1);
  };

  const clearAllChips = () => {
    setSearch("");
    setStatusFilter("");
    setAppliedFilters(emptyFilter);
    setPage(1);
  };

  // sorting
  const onSort = (field) => {
    if (sortBy === field) {
      setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const resetAiSearch = () => {
    if (aiSearchPreview) URL.revokeObjectURL(aiSearchPreview);
    setAiSearchFile(null);
    setAiSearchPreview("");
    setAiSearchResults([]);
    setAiSearchActive(false);
    setAiSearchOpen(false);
  };

  const handleAiFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (aiSearchPreview) URL.revokeObjectURL(aiSearchPreview);

    setAiSearchFile(file);
    setAiSearchPreview(URL.createObjectURL(file));
  };

  const handleAiSearchSubmit = async () => {
    try {
      if (!aiSearchFile) {
        toast.error("Please select an image first.");
        return;
      }

      const res = await visualSearchLabelledItems({
        image: aiSearchFile,
        categoryId: appliedFilters.categoryId || "",
        productId: appliedFilters.productId || "",
        designId: appliedFilters.designId || "",
        purityId: appliedFilters.purityId || "",
        limit: aiSearchLimit || 20,
      }).unwrap();

      setAiSearchResults(Array.isArray(res?.data) ? res.data : []);
      setAiSearchActive(true);
      setAiSearchOpen(false);

      if (!res?.data?.length) {
        toast("No similar items found.");
      } else {
        toast.success(`Found ${res.data.length} matching item(s).`);
      }
    } catch (err) {
      toast.error(err?.data?.msg || err?.error || "AI search failed");
    }
  };

  const shownRows = aiSearchActive ? aiSearchResults : rowsFromStore;

  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[8px]" />
      {/* {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null} */}
    </span>
  );

  // selections
  const selectedRows = useMemo(
    () => Array.from(selectedItems.values()),
    [selectedItems],
  );
  const selectedCount = selectedRows.length;

  const showActions = selectedCount === 0;

  // ✅ checkbox + 18 data cols = 19
  const BASE_TABLE_COLS = 19;

  // ✅ Action column only when showActions=true
  const columnCount = BASE_TABLE_COLS + (showActions ? 1 : 0);

  const toggleSelect = (item) => {
    if (!item?._id) return;
    const key = String(item._id);
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, item);
      return next;
    });
  };

  const removeSelection = (id) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      next.delete(String(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedItems(new Map());

  // select all per page (only deletable)
  const deletableItemsOnPage = (shownRows || []).filter(isDeletable);

  const allSelectedOnPage =
    deletableItemsOnPage.length > 0 &&
    deletableItemsOnPage.every((it) => selectedItems.has(String(it._id)));

  const toggleSelectAllOnPage = () => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (allSelectedOnPage) {
        deletableItemsOnPage.forEach((it) => next.delete(String(it._id)));
      } else {
        deletableItemsOnPage.forEach((it) => next.set(String(it._id), it));
      }
      return next;
    });
  };

  // RTK mutations
  const [deleteItem, { isLoading: deletingOne }] =
    useDeleteLabelledItemMutation();
  const [bulkDelete, { isLoading: deletingMany }] =
    useBulkDeleteLabelledMutation();

  // WhatsApp text builder
  const buildWhatsappText = (rows, note) => {
    const header = note?.trim()
      ? note.trim()
      : "Hi, please check these selected jewellery items.";

    const lines = rows.map((item, idx) => {
      const firstImage =
        Array.isArray(item.images) && item.images.length > 0
          ? item.images[0].url
          : "";

      return [
        `#${idx + 1}`,
        `*Code:* ${item.itemCode || "-"}`,
        `*Product:* ${item.productName || "-"} (${item.categoryName || "-"})`,
        `*Design:* ${item.designName || "-"}`,
        `*Purity:* ${item.purityName || "-"}`,
        `*Wt:* ${item.netWt || 0} g (Total: ${item.totalWt || 0} g)`,
        item.branchName ? `*Branch:* ${item.branchName}` : "",
        firstImage ? `Image: ${firstImage}` : "",
        "-----------------------------",
      ]
        .filter(Boolean)
        .join("\n");
    });

    return header + "\n\n" + lines.join("\n");
  };

  const handleShareWhatsapp = async () => {
    if (!selectedRows.length) {
      toast.error("Please select at least one item to share.");
      return;
    }

    const text = buildWhatsappText(selectedRows, shareNote);
    const encoded = encodeURIComponent(text);
    const url = `https://wa.me/?text=${encoded}`;

    try {
      if (url.length > 1800 && navigator?.clipboard) {
        await navigator.clipboard.writeText(text);
        window.open("https://wa.me/", "_blank");
        toast.success(
          "Full message clipboard me copy kar diya. WhatsApp me jaake sirf paste kar do.",
        );
      } else {
        window.open(url, "_blank");
      }
    } catch (err) {
      console.error("whatsapp share error", err);
      window.open(url, "_blank");
    }

    setShareOpen(false);
  };

  // ✅ helper: rounded rect path for canvas
  function roundedRectPath(ctx, x, y, w, h, r) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
  }

  // ✅ helper: load image and make "cover-cropped" rounded dataURL (no distortion)
  async function loadImageCoverDataUrl(
    url,
    targetWmm,
    targetHmm,
    radiusMm = 3,
  ) {
    // mm -> px at ~144dpi (sharper than 96)
    const dpi = 144;
    const mmToPx = (mm) => Math.round((mm / 25.4) * dpi);

    const tw = mmToPx(targetWmm);
    const th = mmToPx(targetHmm);
    const r = mmToPx(radiusMm);

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = tw;
          canvas.height = th;
          const ctx = canvas.getContext("2d");

          // background
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, tw, th);

          // clip rounded
          roundedRectPath(ctx, 0, 0, tw, th, r);
          ctx.clip();

          // cover crop
          const scale = Math.max(tw / img.width, th / img.height);
          const dw = img.width * scale;
          const dh = img.height * scale;
          const dx = (tw - dw) / 2;
          const dy = (th - dh) / 2;

          ctx.drawImage(img, dx, dy, dw, dh);

          // export
          resolve(canvas.toDataURL("image/jpeg", 0.92));
        } catch (e) {
          console.error("cover canvas error", e);
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  // ✅ PDF Catalog download (Luxury Brochure)
const handleDownloadCatalogPdf = async () => {
  if (!selectedRows.length) {
    toast.error("Please select at least one item for catalog.");
    return;
  }

  try {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const COLORS = {
      navy: [15, 23, 42],
      cream: [250, 247, 240],
      gold: [196, 151, 76],
      softGold: [235, 219, 180],
      white: [255, 255, 255],
      muted: [100, 116, 139],
      dark: [30, 41, 59],
      line: [229, 221, 204],
    };

    const items = [...selectedRows].sort((a, b) => {
      const ac = (a.categoryName || "").localeCompare(b.categoryName || "");
      if (ac !== 0) return ac;
      return (a.designName || a.productName || "").localeCompare(
        b.designName || b.productName || "",
      );
    });

    const getImage = async (item, w, h) => {
      const url = item?.images?.[0]?.url;
      if (!url) return null;
      return await loadImageCoverDataUrl(url, w, h, 3);
    };

    const addFooter = (pageNo) => {
      doc.setDrawColor(...COLORS.line);
      doc.line(18, pageHeight - 14, pageWidth - 18, pageHeight - 14);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.muted);
      doc.text("Premium Jewellery Catalogue", 18, pageHeight - 8);
      doc.text(`Page ${pageNo}`, pageWidth - 18, pageHeight - 8, {
        align: "right",
      });
    };

    const drawProductDetails = (item, x, y, width) => {
      const title = item.designName || item.productName || "Jewellery Design";

      doc.setFont("times", "bold");
      doc.setFontSize(20);
      doc.setTextColor(...COLORS.dark);
      doc.text(doc.splitTextToSize(title, width), x, y);

      y += 12;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.muted);
      doc.text(
        doc.splitTextToSize(
          `${item.categoryName || "-"} • ${item.productName || "-"} • ${
            item.purityName || "-"
          }`,
          width,
        ),
        x,
        y,
      );

      y += 12;

      doc.setDrawColor(...COLORS.softGold);
      doc.line(x, y, x + width, y);

      y += 9;

      const details = [
        ["Item Code", item.itemCode || "-"],
        ["Net Weight", `${item.netWt ?? "-"} g`],
        ["Total Weight", `${item.totalWt ?? "-"} g`],
        ["Gross Weight", `${item.grossWt ?? "-"} g`],
      ];

      details.forEach(([label, value], index) => {
        const rowY = y + index * 8;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.muted);
        doc.text(label, x, rowY);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...COLORS.dark);
        doc.text(String(value), x + width, rowY, { align: "right" });
      });
    };

    const drawCoverPage = async () => {
      doc.setFillColor(...COLORS.navy);
      doc.rect(0, 0, pageWidth, pageHeight, "F");

      const heroItem = items.find((x) => x?.images?.length) || items[0];
      const heroImg = await getImage(heroItem, 150, 150);

      if (heroImg) {
        doc.addImage(heroImg, "JPEG", 30, 34, 150, 150);
      }

      doc.setFillColor(...COLORS.navy);
      doc.rect(0, 188, pageWidth, 109, "F");

      doc.setDrawColor(...COLORS.gold);
      doc.setLineWidth(0.8);
      doc.line(42, 202, pageWidth - 42, 202);

      doc.setFont("times", "bold");
      doc.setFontSize(30);
      doc.setTextColor(...COLORS.white);
      doc.text("JEWELLERY", pageWidth / 2, 222, { align: "center" });
      doc.text("LOOKBOOK", pageWidth / 2, 238, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.softGold);
      doc.text("Curated Premium Collection", pageWidth / 2, 250, {
        align: "center",
      });

      const dateStr = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

      doc.setFontSize(8);
      doc.setTextColor(210, 210, 210);
      doc.text(`${items.length} Selected Designs • ${dateStr}`, pageWidth / 2, 265, {
        align: "center",
      });
    };

    const drawSingleShowcasePage = async (item, pageNo) => {
      doc.setFillColor(...COLORS.cream);
      doc.rect(0, 0, pageWidth, pageHeight, "F");

      doc.setFillColor(...COLORS.white);
      doc.roundedRect(16, 18, pageWidth - 32, pageHeight - 42, 6, 6, "F");

      const img = await getImage(item, 160, 145);

      if (img) {
        doc.addImage(img, "JPEG", 25, 28, 160, 145);
      }

      doc.setDrawColor(...COLORS.gold);
      doc.setLineWidth(0.5);
      doc.roundedRect(25, 28, 160, 145, 4, 4, "S");

      drawProductDetails(item, 28, 195, pageWidth - 56);

      addFooter(pageNo);
    };

    const drawEditorialPage = async (item, pageNo) => {
      doc.setFillColor(...COLORS.cream);
      doc.rect(0, 0, pageWidth, pageHeight, "F");

      const img = await getImage(item, 105, 210);

      if (img) {
        doc.addImage(img, "JPEG", 16, 28, 105, 210);
      }

      doc.setFillColor(...COLORS.white);
      doc.roundedRect(116, 48, 78, 150, 5, 5, "F");

      doc.setDrawColor(...COLORS.gold);
      doc.setLineWidth(0.5);
      doc.line(130, 64, 180, 64);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.gold);
      doc.text("FEATURED DESIGN", 155, 58, { align: "center" });

      drawProductDetails(item, 126, 82, 58);

      addFooter(pageNo);
    };

    const drawDualShowcasePage = async (itemA, itemB, pageNo) => {
      doc.setFillColor(...COLORS.cream);
      doc.rect(0, 0, pageWidth, pageHeight, "F");

      const drawOne = async (item, x) => {
        doc.setFillColor(...COLORS.white);
        doc.roundedRect(x, 28, 82, 218, 5, 5, "F");

        const img = await getImage(item, 70, 95);

        if (img) {
          doc.addImage(img, "JPEG", x + 6, 36, 70, 95);
        }

        doc.setDrawColor(...COLORS.softGold);
        doc.roundedRect(x + 6, 36, 70, 95, 3, 3, "S");

        drawProductDetails(item, x + 8, 152, 66);
      };

      await drawOne(itemA, 18);
      if (itemB) await drawOne(itemB, 110);

      addFooter(pageNo);
    };

    const drawClosingPage = () => {
      doc.setFillColor(...COLORS.navy);
      doc.rect(0, 0, pageWidth, pageHeight, "F");

      doc.setDrawColor(...COLORS.gold);
      doc.setLineWidth(0.8);
      doc.roundedRect(24, 38, pageWidth - 48, pageHeight - 76, 8, 8, "S");

      doc.setFont("times", "bold");
      doc.setFontSize(28);
      doc.setTextColor(...COLORS.white);
      doc.text("THANK YOU", pageWidth / 2, 110, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...COLORS.softGold);
      doc.text("For exploring our curated jewellery collection", pageWidth / 2, 126, {
        align: "center",
      });

      doc.setFontSize(9);
      doc.setTextColor(220, 220, 220);
      doc.text("Contact showroom for price, availability and customization.", pageWidth / 2, 158, {
        align: "center",
      });

      doc.setFontSize(8);
      doc.text("Generated by Sparkle ERP", pageWidth / 2, pageHeight - 42, {
        align: "center",
      });
    };

    await drawCoverPage();

    let pageNo = 1;
    let i = 0;

    while (i < items.length) {
      doc.addPage();
      pageNo++;

      if (i % 4 === 0) {
        await drawSingleShowcasePage(items[i], pageNo);
        i += 1;
      } else if (i % 4 === 1) {
        await drawEditorialPage(items[i], pageNo);
        i += 1;
      } else {
        await drawDualShowcasePage(items[i], items[i + 1], pageNo);
        i += 2;
      }
    }

    doc.addPage();
    drawClosingPage();

    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    setCatalogPreviewUrl(url);
    setCatalogPreviewOpen(true);
    toast.success("Luxury brochure preview ready.");
  } catch (err) {
    console.error("catalog pdf error", err);
    toast.error("Failed to generate catalog PDF.");
  }
};

  // LABEL PRINT
  const openLabelPrintForItems = async (items) => {
    if (!items || !items.length) {
      toast.error("No items to print labels.");
      return;
    }

    if (!labelTemplates.length) {
      toast.error("No label templates found.");
      return;
    }

    try {
      const groups = new Map();

      for (const item of items) {
        const template = pickTemplateForItem(labelTemplates, item);
        if (!template) continue;

        const layout = template.layout || template;
        const key = String(
          layout._id || template._id || layout.id || "default",
        );

        if (!groups.has(key)) {
          groups.set(key, { template: layout, items: [] });
        }

        groups.get(key).items.push(item);
      }

      if (!groups.size) {
        toast.error("No matching label template for selected items.");
        return;
      }

      for (const { template, items: groupItems } of groups.values()) {
        if (!groupItems.length) continue;

        const templateType = String(template?.templateType || "LabelledItem");

        // ✅ PRN template => .prn file download
        if (templateType === "prn") {
          if (!template?.prnCode) {
            toast.error(`PRN code missing in template ${template?.name || ""}`);
            continue;
          }

          const combinedContent = groupItems
            .map((item) =>
              fillPrnTemplate(template.prnCode, {
                ...item,
                ItemCode: item.itemCode,
                ProductCode: item.productCode,
                ProductName: item.productName,
                CategoryName: item.categoryName,
                DesignName: item.designName,
                PurityName: item.purityName,
                GrossWt: item.grossWt,
                NetWt: item.netWt,
                TotalWt: item.totalWt,
                MRP: item.mrp,
                RFIDCode: item.rfidCode,
                EPCCode: item.epcCode,
              }),
            )
            .join("\n");

          downloadPrnFile({
            items: groupItems,
            prnCode: combinedContent,
          });
        } else {
          // ✅ Normal template => existing PDF flow
          await generateLabelsPdfForStock(groupItems, template);
        }
      }
    } catch (err) {
      console.error("Label/PRN print error", err);
      toast.error("Failed to generate label output.");
    }
  };

  const handlePrintLabel = (item) => {
    if (!item) return;
    openLabelPrintForItems([item]);
  };

  const handleBulkPrintLabels = () => {
    if (!selectedRows.length) {
      toast.error("Please select at least one item to print labels.");
      return;
    }
    openLabelPrintForItems(selectedRows);
  };

  // confirm handlers
  const openConfirmSingle = (item) => {
    if (!isDeletable(item)) return;

    const sameDay = isSameDayDelete(item.createdAt);

    setConfirm({
      open: true,
      mode: "single",
      payload: {
        id: item._id,
        label: item.itemCode,
        sameDay,
      },
    });
  };

  const openConfirmBulk = () => {
    const rows = Array.from(selectedItems.values());
    const ids = rows.map((x) => x._id).filter(Boolean);

    if (!ids.length) return;

    const oldItemsCount = rows.filter(
      (x) => !isSameDayDelete(x.createdAt),
    ).length;

    setConfirm({
      open: true,
      mode: "bulk",
      payload: {
        ids,
        count: ids.length,
        oldItemsCount,
        hasOldItems: oldItemsCount > 0,
      },
    });
  };

  const closeConfirm = () =>
    setConfirm({ open: false, mode: null, payload: null });

  const handleConfirm = async () => {
    try {
      if (confirm.mode === "single") {
        const { id, label } = confirm.payload || {};
        await deleteItem(id).unwrap();
        toast.success(`Deleted ${label}`);
        removeSelection(id);
      } else if (confirm.mode === "bulk") {
        const { ids } = confirm.payload || {};
        const res = await bulkDelete(ids).unwrap();
        const skipped = (res?.blocked || []).length;
        toast.success(
          `Deleted ${res?.deleted || 0}${
            skipped ? ` • Skipped ${skipped}` : ""
          }`,
        );
        clearSelection();
      }
    } catch (e) {
      const msg = e?.data?.msg || e?.error || "Operation failed";
      toast.error(msg);
    } finally {
      closeConfirm();
    }
  };

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* Global loader overlay */}
      {isFetching && !shownRows.length && !aiSearchActive && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading labelled stock…</span>
          </div>
        </div>
      )}

      {/* Top header */}
      <div className="flex items-center justify-between px-4 py-2 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold tracking-tight">
            Labelled Stock
          </h2>
          {isFetching && rowsFromStore.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* <motion.button
            type="button"
            onClick={() => setAiSearchOpen(true)}
            initial={false}
            animate={{
              y: [0, -2, 0],
              scale: [1, 1.02, 1],
              boxShadow: [
                "0 8px 20px rgba(109, 79, 216, 0.18)",
                "0 12px 28px rgba(109, 79, 216, 0.28)",
                "0 8px 20px rgba(109, 79, 216, 0.18)",
              ],
            }}
            whileHover={{
              scale: 1.06,
              y: -2,
              boxShadow: "0 14px 34px rgba(109, 79, 216, 0.35)",
            }}
            whileTap={{ scale: 0.96 }}
            transition={{
              duration: 2.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="group relative overflow-hidden inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-violet-600 via-fuchsia-500 to-indigo-600 border border-white/20"
          >
            <span className="absolute inset-0 rounded-full bg-white/10 blur-md group-hover:bg-white/20 transition" />

            <motion.span
              aria-hidden="true"
              className="absolute top-0 -left-[30%] h-full w-[30%] bg-gradient-to-r from-transparent via-white/35 to-transparent skew-x-[-20deg]"
              animate={{ left: ["-30%", "130%"] }}
              transition={{
                duration: 2.2,
                repeat: Infinity,
                repeatDelay: 1.2,
                ease: "easeInOut",
              }}
            />

            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-cyan-200" />
            </span>

            <motion.span
              animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.08, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <FaImage className="text-[12px]" />
            </motion.span>

            <span className="relative tracking-wide">AI Search</span>

            <motion.span
              animate={{ x: [0, 3, 0] }}
              transition={{
                duration: 1.4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="relative text-[11px]"
            >
              ✦
            </motion.span>
          </motion.button>
          {aiSearchActive && (
            <button
              type="button"
              onClick={() => {
                setAiSearchActive(false);
                setAiSearchResults([]);
                if (aiSearchPreview) URL.revokeObjectURL(aiSearchPreview);
                setAiSearchPreview("");
                setAiSearchFile(null);
              }}
              className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-600 px-4 py-1.5 text-xs font-semibold shadow-sm hover:bg-rose-100 transition"
            >
              <FaTimesCircle className="text-[11px]" />
              Clear AI
            </button>
          )} */}

          <Link
            to="/stocks/labelled-stock-list/create-labelled-stock"
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#6b4fd8] px-4 py-1.5 text-xs font-semibold shadow-sm hover:bg-violet-50 transition"
          >
            <span>+ Add New</span>
            <span className="rounded border border-violet-200 bg-violet-50 px-1 py-[1px] text-[9px] font-semibold text-violet-600 leading-none">
              Ctrl + P
            </span>
          </Link>
        </div>
      </div>

      {/* Filters bar */}
      <div className="px-2 sm:px-4 py-2 border-b bg-slate-50/80">
        <div className="flex items-end gap-2 sm:flex-wrap sm:gap-3">
          {/* Status */}
          <div className="flex w-[70px] shrink-0 flex-col gap-[1px] sm:w-[130px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block">
              Status
            </label>
            <select
              aria-label="Filter by status"
              title="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="h-8 w-full rounded-lg border border-slate-200 px-1 text-[10px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400 sm:px-2.5 sm:text-[11px]"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="sold">Sold</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* View toggle */}
          <div className="flex w-[66px] shrink-0 flex-col gap-[1px] sm:w-[150px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block">
              View
            </label>
            <div className="flex h-8 overflow-hidden rounded-full border border-slate-200 bg-white text-[11px]">
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-full px-1 font-medium outline-[#6b59c6] transition sm:px-3 ${
                  viewMode === "table"
                    ? "bg-[#6b59c6]/10 text-[#6b59c6]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                title="List view"
                aria-label="List view"
              >
                <FaList className="text-[11px]" />
                <span className="hidden sm:inline">Table</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-full px-1 font-medium outline-[#6b59c6] transition sm:px-3 ${
                  viewMode === "grid"
                    ? "bg-[#6b59c6]/10 text-[#6b59c6]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                title="Grid view"
                aria-label="Grid view"
              >
                <FaThLarge className="text-[11px]" />
                <span className="hidden sm:inline">Grid</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="flex min-w-0 flex-1 flex-col gap-[1px] sm:min-w-[160px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block">
              Search
            </label>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 min-w-0 w-full rounded-lg border border-slate-200 px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400 sm:px-3"
            />
          </div>

          {/* Filters button + bulk menu */}
          <div className="flex w-auto shrink-0 flex-col gap-[1px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block sm:invisible">
              Filters
            </label>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={openFilters}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center gap-1 rounded-full border border-violet-200 bg-white text-[11px] text-[#6b59c6] outline-[#6b59c6] hover:bg-violet-50 sm:w-auto sm:px-3"
                title="Advanced filters"
                aria-label="Advanced filters"
              >
                <FaFilter /> <span className="hidden sm:inline">Filters</span>
              </button>

              {/* Bulk menu */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setBulkMenuOpen((p) => !p)}
                  className="flex items-center justify-center h-8 w-8 rounded-full bg-white border border-slate-200 hover:bg-slate-50 outline-[#6b59c6]"
                  title="Bulk actions"
                >
                  <FiMoreVertical className="text-sm text-slate-600" />
                </button>

                {bulkMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white text-gray-800 rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden">
                    <div className="px-3 py-2 text-[11px] text-slate-500 border-b bg-slate-50/70 flex items-center justify-between">
                      <span className="font-semibold tracking-wide">
                        Bulk actions
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="h-5 min-w-[20px] px-2 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold flex items-center justify-center">
                          {selectedCount || 0}
                        </span>
                        <span className="text-[10px]">selected</span>
                      </span>
                    </div>

                    <div className="py-1">
                      <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400">
                        Share & Export
                      </p>

                      <button
                        type="button"
                        onClick={() => {
                          setBulkMenuOpen(false);
                          setShareOpen(true);
                        }}
                        disabled={!selectedCount}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-violet-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <span className="text-base">📲</span>
                        <span>Share on WhatsApp</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setBulkMenuOpen(false);
                          handleDownloadCatalogPdf();
                        }}
                        disabled={!selectedCount}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-violet-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <span className="text-base">📄</span>
                        <span>Download catalog PDF</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setBulkMenuOpen(false);
                          handleBulkPrintLabels();
                        }}
                        disabled={!selectedCount}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-violet-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <span className="text-base">🏷️</span>
                        <span>Print labels</span>
                      </button>
                    </div>

                    <div className="mt-1 border-t border-slate-100 bg-rose-50/40">
                      <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-rose-500/80">
                        Danger zone
                      </p>

                      <button
                        type="button"
                        onClick={() => {
                          if (canDelete(user)) {
                            setBulkMenuOpen(false);
                            openConfirmBulk();
                          } else {
                            toast.error("You don't have permission to delete");
                          }
                        }}
                        disabled={!selectedCount || deletingMany}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed text-rose-600 flex items-center gap-2"
                      >
                        <FaTrashAlt className="text-xs" />
                        <span>Delete selected</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm"
            >
              {c.label}
              <button
                className="text-slate-500 hover:text-red-500"
                onClick={() => clearChip(c.key)}
                title="Clear"
              >
                <FaTimesCircle />
              </button>
            </span>
          ))}
          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      {aiSearchActive && (
        <div className="px-4 py-2 border-b bg-violet-50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] text-violet-700">
            <FaSearch />
            <span className="font-semibold">
              Showing AI matched items ({shownRows.length})
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setAiSearchActive(false);
              setAiSearchResults([]);
              if (aiSearchPreview) URL.revokeObjectURL(aiSearchPreview);
              setAiSearchPreview("");
              setAiSearchFile(null);
            }}
            className="text-[11px] text-rose-500 hover:text-rose-600"
          >
            Clear results
          </button>
        </div>
      )}
      {/* Selected items bar */}
      {selectedCount > 0 && (
        <div className="px-4 py-2.5 border-b bg-gradient-to-r from-violet-50 via-indigo-50 to-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="h-6 w-6 rounded-full bg-[#6b59c6] text-white flex items-center justify-center text-[11px] shadow-sm">
                {selectedCount}
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[11px] font-semibold text-slate-800">
                  {selectedCount} item{selectedCount > 1 ? "s" : ""} selected
                </span>
                <span className="text-[10px] text-slate-500">
                  Use bulk actions to share, export or delete
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max pr-2">
                {selectedRows.map((item) => (
                  <button
                    key={item._id}
                    type="button"
                    className="group inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/80 border border-violet-100 text-[11px] text-slate-700 shadow-[0_1px_3px_rgba(15,23,42,0.06)] hover:border-[#6b59c6]/70 hover:bg-violet-50/80 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <span className="font-medium text-[#6b59c6]">
                      {item.itemCode || "—"}
                    </span>
                    {item.productName && (
                      <span className="text-[10px] text-slate-500">
                        · {item.productName}
                      </span>
                    )}

                    <FaTimesCircle
                      className="ml-1 text-[10px] text-slate-300 group-hover:text-rose-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSelection(item._id);
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={clearSelection}
              className="ml-2 text-[11px] text-rose-500 hover:text-rose-600 hover:bg-rose-50 px-3 py-1 rounded-full border border-rose-100 bg-white flex items-center gap-1 flex-shrink-0"
            >
              <FaTimesCircle className="text-[11px]" />
              <span>Clear all</span>
            </button>
          </div>
        </div>
      )}

      {/* ===== Main Content: TABLE or GRID ===== */}
      {viewMode === "table" ? (
        <div className="relative flex-1 min-h-0 overflow-x-auto overflow-y-auto">
          <table className="min-w-full text-xs sm:text-sm text-left">
            <thead className="sticky top-0 z-40 bg-[#e6e1f5] text-[10px] uppercase text-[#4b3d91] sm:text-[11px]">
              <tr>
                <th className="px-3 py-1.5 w-10">
                  <input
                    type="checkbox"
                    checked={allSelectedOnPage}
                    onChange={toggleSelectAllOnPage}
                    className="h-4 w-4"
                    title="Select all deletable on page"
                  />
                </th>

                <th className={`${headerCell} text-center`}>
                  Item Code {sortIcon("itemCode")}
                </th>
                <th className={headerCell}>Product Code</th>
                <th className={headerCell}>Branch </th>
                <th className={headerCell}>Counter</th>
                <th className={headerCell}>Category</th>
                <th className={headerCell}>Product</th>

                <th className={headerCell}>Design</th>
                <th className={headerCell}>Purity {sortIcon("purityName")}</th>
                <th className={`${headerCell} text-right`}>
                  Total Wt {sortIcon("totalWt")}
                </th>
                <th className={`${headerCell} text-right`}>Packing Wt</th>
                <th className={`${headerCell} text-right`}>Gross Wt</th>
                <th className={`${headerCell} text-right`}>Stone Wt</th>
                <th className={`${headerCell} text-right`}>Diamond Wt</th>
                <th className={`${headerCell} text-center`}>
                  Net Wt {sortIcon("netWt")}
                </th>
                <th className={headerCell}>Status</th>
                <th className={headerCell}>
                  Created On {sortIcon("createdAt")}
                </th>
                <th className={`${headerCell} text-right`}>Fixed Making</th>
                <th className={`${headerCell} text-right`}>Making/Grm</th>
                <th className={`${headerCell} text-right`}>Making %</th>

                {showActions && (
                  <th
                    className={`${headerCell} sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px]`}
                  >
                    <span className="sm:hidden">Act</span>
                    <span className="hidden sm:inline">Action</span>
                  </th>
                )}
              </tr>
            </thead>

            <tbody className="bg-white text-gray-700">
              <AnimatePresence initial={false}>
                {isFetching && shownRows.length && !aiSearchActive ? (
                  <SkeletonRows
                    cols={columnCount}
                    rows={8}
                    actionColumn={showActions}
                  />
                ) : shownRows?.length > 0 ? (
                  shownRows.map((item, index) => {
                    const deletable = isDeletable(item);
                    const checked = selectedItems.has(String(item._id));
                    return (
                      <motion.tr
                        key={item._id || `${item.itemCode}-${index}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        className="group border-t transition hover:bg-[#f4f0ff]"
                      >
                        <td className="px-3 sm:px-4 py-1.5">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={checked}
                            disabled={!deletable}
                            title={
                              !deletable
                                ? "Only active items (and not deactivated) can be deleted"
                                : "Select item"
                            }
                            onChange={() => toggleSelect(item)}
                          />
                        </td>

                        <td className={`${dataCell} font-medium`}>
                          <div className="inline-block px-3 py-1 rounded-full border bg-white text-center shadow-sm text-[11px]">
                            {item.itemCode}
                          </div>
                        </td>
                        <td className={dataCell}>{item.productCode}</td>
                        <td className={dataCell}>{item.counterName}</td>
                        <td className={dataCell}>{item.branchName}</td>
                        <td className={dataCell}>{item.categoryName}</td>
                        <td className={dataCell}>{item.productName}</td>
                        <td className={dataCell}>{item.designName}</td>
                        <td className={dataCell}>{item.purityName}</td>
                        <td className={`${dataCell} text-right`}>
                          {item.totalWt}
                        </td>
                        <td className={`${dataCell} text-right`}>
                          {item.packingWt}
                        </td>
                        <td className={`${dataCell} text-right`}>
                          {item.grossWt}
                        </td>
                        <td className={`${dataCell} text-right`}>
                          {item.stoneWt}
                        </td>
                        <td className={`${dataCell} text-right`}>
                          {item.diamondWt}
                        </td>
                        <td className={`${dataCell} text-right`}>
                          {item.netWt}
                        </td>

                        <td className={dataCell}>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] ${
                              item.status === "active"
                                ? "bg-emerald-100 text-emerald-700"
                                : item.status === "sold"
                                  ? "bg-rose-100 text-rose-700"
                                  : item.status === "inactive"
                                    ? "bg-slate-200 text-slate-700"
                                    : "bg-violet-100 text-violet-700"
                            }`}
                          >
                            {toTitle(item.status || "")}
                          </span>
                        </td>
                        <td className={dataCell}>{fmtDate(item.createdAt)}</td>
                        <td className={`${dataCell} text-right`}>
                          {item.fixedMaking}
                        </td>
                        <td className={`${dataCell} text-right`}>
                          {item.makingPerGram}
                        </td>
                        <td className={`${dataCell} text-right`}>
                          {item.makingPercentage}
                        </td>

                        {showActions && (
                          <td
                            className={`${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px]`}
                          >
                            <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              title="Edit"
                              onClick={() =>
                                navigate(
                                  `/stocks/labelled-stock-list/edit/${item._id}`,
                                )
                              }
                            >
                              <FaPencilAlt />
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-violet-100 text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              title="Print label"
                              onClick={() => handlePrintLabel(item)}
                            >
                              <FaPrint />
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 disabled:opacity-40 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              title="Delete"
                              disabled={deletingOne || !deletable}
                              onClick={() => {
                                if (canDelete(user)) {
                                  openConfirmSingle(item);
                                } else {
                                  toast.error(
                                    "You don't have permission to delete",
                                  );
                                }
                              }}
                            >
                              <FaTrashAlt />
                            </motion.button>
                            </div>
                          </td>
                        )}
                      </motion.tr>
                    );
                  })
                ) : (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td
                      colSpan={columnCount}
                      className="px-4 py-8 text-center text-xs text-slate-500"
                    >
                      No labelled stock found. Try changing filters or search
                      text.
                    </td>
                  </motion.tr>
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {isFetching && shownRows.length && !aiSearchActive ? (
            <GridSkeleton count={8} />
          ) : shownRows.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {shownRows.map((item, index) => {
                const deletable = isDeletable(item);
                const checked = selectedItems.has(String(item._id));
                return (
                  <motion.div
                    key={item._id || `${item.itemCode}-${index}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, delay: index * 0.02 }}
                    className="relative border rounded-2xl bg-white shadow-sm overflow-hidden flex flex-col"
                  >
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 bg-white"
                        checked={checked}
                        disabled={!deletable}
                        onChange={() => toggleSelect(item)}
                      />
                    </div>

                    <div className="absolute top-2 right-2 z-10">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          item.status === "active"
                            ? "bg-emerald-100 text-emerald-700"
                            : item.status === "sold"
                              ? "bg-rose-100 text-rose-700"
                              : item.status === "inactive"
                                ? "bg-slate-200 text-slate-700"
                                : "bg-violet-100 text-violet-700"
                        }`}
                      >
                        {toTitle(item.status || "")}
                      </span>
                    </div>

                    <ImageCarousel images={item.images || []} />

                    <div className="p-3 flex flex-col gap-2 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold text-slate-800 bg-slate-50">
                          <span>{item.itemCode}</span>
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {fmtDate(item.createdAt)}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-semibold text-slate-900 line-clamp-1">
                          {item.designName || item.productName || "Item"}
                        </div>
                        <div className="text-[11px] text-slate-500 line-clamp-2">
                          {item.categoryName || "-"} · {item.productName || "-"}{" "}
                          · {item.purityName || "Purity"}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-600">
                        <div>
                          <div className="text-[10px] text-slate-400">
                            Total Wt
                          </div>
                          <div className="font-semibold">
                            {item.totalWt ?? "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-slate-400">
                            Net Wt
                          </div>
                          <div className="font-semibold">
                            {item.netWt ?? "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-slate-400">
                            Branch
                          </div>
                          <div className="font-semibold line-clamp-1">
                            {item.branchName || "-"}
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/stocks/labelled-stock-list/edit/${item._id}`,
                            )
                          }
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-50 text-[11px] text-violet-700 hover:bg-violet-100"
                        >
                          <FaPencilAlt className="text-[10px]" />
                          Edit
                        </button>

                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="Print label"
                            onClick={() => handlePrintLabel(item)}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-50 text-[#9481ce] hover:bg-violet-50"
                          >
                            <FaPrint className="text-[11px]" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            disabled={deletingOne || !deletable}
                            onClick={() => {
                              if (canDelete(user)) {
                                openConfirmSingle(item);
                              } else {
                                toast.error(
                                  "You don't have permission to delete",
                                );
                              }
                            }}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-50 text-rose-500 hover:bg-rose-50 disabled:opacity-40"
                          >
                            <FaTrashAlt className="text-[11px]" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-xs text-slate-500">
              No labelled stock found. Try adjusting filters.
            </div>
          )}
        </div>
      )}

      {/* Footer – pagination */}
      <div className="shrink-0 border-t px-2 py-1.5 sm:px-4 sm:py-2 bg-slate-50 rounded-b-2xl flex items-center justify-between gap-2 sm:gap-3 text-[10px] sm:text-[11px]">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="text-slate-600">
            Showing{" "}
            <span className="font-semibold">
              {shownRows.length
                ? aiSearchActive
                  ? 1
                  : (page - 1) * limit + 1
                : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {shownRows.length
                ? aiSearchActive
                  ? shownRows.length
                  : (page - 1) * limit + shownRows.length
                : 0}
            </span>{" "}
            of{" "}
            <span className="font-semibold">
              {aiSearchActive ? shownRows.length : totalDocs}
            </span>{" "}
            entries
          </span>
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => setLimit(Number(option?.value) || 10)}
              isDisabled={aiSearchActive}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:w-auto sm:flex-none sm:justify-start sm:gap-1">
            <button
              type="button"
              disabled={aiSearchActive || !hasPrev}
              onClick={() => hasPrev && setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>
            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{
                    value: aiSearchActive ? 1 : totalPages ? page : 1,
                    label: String(
                      aiSearchActive ? 1 : totalPages ? page : 1,
                    ),
                  }}
                  options={Array.from(
                    { length: Math.max(1, aiSearchActive ? 1 : totalPages) },
                    (_, index) => ({
                      value: index + 1,
                      label: String(index + 1),
                    }),
                  )}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={aiSearchActive}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {Math.max(1, aiSearchActive ? 1 : totalPages)}</span>
              </span>
              <span className="hidden items-center gap-1 sm:flex">
                <span>Page</span>
                <Select
                  value={{
                    value: aiSearchActive ? 1 : totalPages ? page : 1,
                    label: String(aiSearchActive ? 1 : totalPages ? page : 1),
                  }}
                  options={Array.from(
                    { length: Math.max(1, aiSearchActive ? 1 : totalPages) },
                    (_, index) => ({
                      value: index + 1,
                      label: String(index + 1),
                    }),
                  )}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={aiSearchActive || isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[58px]"
                  aria-label="Select page number"
                />
                <span>of {Math.max(1, aiSearchActive ? 1 : totalPages)}</span>
              </span>
            </span>
            <button
              type="button"
              disabled={aiSearchActive || !hasNext}
              onClick={() =>
                hasNext &&
                setPage((p) =>
                  totalPages ? Math.min(totalPages, p + 1) : p + 1,
                )
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filter modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Refine labelled stock by vendor, branch, counter, category
                    and date range.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-4 text-[11px]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Vendor */}
                  <div>
                    <label className="block text-slate-600 mb-1">Vendor</label>
                    <Select
                      ref={vendorSelectRef}
                      styles={selectStyles}
                      value={vendorValue}
                      onChange={onVendorChange}
                      options={vendorOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Branch */}
                  <div>
                    <label className="block text-slate-600 mb-1">Branch</label>
                    <Select
                      styles={selectStyles}
                      value={branchValue}
                      onChange={onBranchChange}
                      options={branchOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* ✅ Counter (depends on Branch) */}
                  <div>
                    <label className="block text-slate-600 mb-1">Counter</label>
                    <Select
                      styles={selectStyles}
                      value={counterValue}
                      onChange={onCounterChange}
                      options={counterOptionsDraft}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      placeholder={
                        draftFilters.branchId
                          ? "Select counter"
                          : "All counters (no branch selected)"
                      }
                      // ✅ if branch selected and that branch has 0 counters -> disable
                      isDisabled={
                        !!draftFilters.branchId &&
                        counterOptionsDraft.length === 0
                      }
                      noOptionsMessage={() =>
                        draftFilters.branchId
                          ? "No counters found for selected branch"
                          : "No counters found"
                      }
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Category
                    </label>
                    <Select
                      styles={selectStyles}
                      value={categoryValue}
                      onChange={onCategoryChange}
                      options={categoryOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Product */}
                  <div>
                    <label className="block text-slate-600 mb-1">Product</label>
                    <Select
                      styles={selectStyles}
                      value={productValue}
                      onChange={onProductChange}
                      options={productOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Design */}
                  <div>
                    <label className="block text-slate-600 mb-1">Design</label>
                    <Select
                      styles={selectStyles}
                      value={designValue}
                      onChange={onDesignChange}
                      options={designOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Purity */}
                  <div>
                    <label className="block text-slate-600 mb-1">Purity</label>
                    <Select
                      styles={selectStyles}
                      value={purityValue}
                      onChange={onPurityChange}
                      options={purityOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>
                </div>

                {/* Date range */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">
                      From date
                    </label>
                    <input
                      type="date"
                      name="dateFrom"
                      value={draftFilters.dateFrom}
                      onChange={handleDateChange}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">To date</label>
                    <input
                      type="date"
                      name="dateTo"
                      value={draftFilters.dateTo}
                      onChange={handleDateChange}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetDraftFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="px-3 py-1.5 rounded-md bg-[#6b59c6] text-white text-[11px] font-semibold hover:bg-[#5b4ab3]"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Catalog preview modal */}
      <AnimatePresence>
        {catalogPreviewOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Catalog preview
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Review the PDF before downloading or sharing.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={closeCatalogPreview}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="flex-1 bg-slate-100">
                {catalogPreviewUrl ? (
                  <iframe
                    title="Catalog preview"
                    src={catalogPreviewUrl}
                    className="w-full h-[70vh] bg-slate-200"
                  />
                ) : (
                  <div className="w-full h-[70vh] flex items-center justify-center text-xs text-slate-500">
                    Generating preview…
                  </div>
                )}
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <span className="text-[11px] text-slate-500">
                  {selectedCount} item{selectedCount !== 1 ? "s" : ""} in this
                  catalog
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeCatalogPreview}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadCatalogFromPreview}
                    className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700"
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp share modal */}
      <AnimatePresence>
        {shareOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800">
                  Share selected items
                </h3>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setShareOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-3 text-[11px]">
                <div>
                  <label className="block text-slate-600 mb-1">
                    Note (optional)
                  </label>
                  <textarea
                    rows={3}
                    value={shareNote}
                    onChange={(e) => setShareNote(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-violet-400 text-[11px]"
                  />
                </div>

                <div>
                  <p className="text-slate-500">
                    We will open WhatsApp with a pre-filled message containing
                    the selected designs and their basic details.
                  </p>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShareOpen(false)}
                  className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleShareWhatsapp}
                  className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700"
                >
                  Share on WhatsApp
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {aiSearchOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Search Similar Design
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Upload an image and find matching stock items.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={resetAiSearch}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-4 text-[11px]">
                <div>
                  <label className="block text-slate-600 mb-1">
                    Upload image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAiFileChange}
                    className="w-full border border-slate-200 rounded-lg px-2 py-2 text-[11px]"
                  />
                </div>

                {aiSearchPreview && (
                  <div className="border rounded-xl overflow-hidden bg-slate-50">
                    <img
                      src={aiSearchPreview}
                      alt="Search preview"
                      className="w-full h-56 object-contain"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-slate-600 mb-1">
                    Max results
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={aiSearchLimit}
                    onChange={(e) =>
                      setAiSearchLimit(Number(e.target.value) || 20)
                    }
                    className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-[11px]"
                  />
                </div>

                <div className="text-[10px] text-slate-500">
                  Current applied category/product/design/purity filters will
                  also be used in AI search.
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={resetAiSearch}
                  className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAiSearchSubmit}
                  disabled={searchingAi}
                  className="px-3 py-1.5 rounded-md bg-[#6b59c6] text-white text-[11px] font-semibold hover:bg-[#5b4ab3] disabled:opacity-50"
                >
                  {searchingAi ? "Searching..." : "Find Similar"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirm.open}
        onCancel={closeConfirm}
        onConfirm={handleConfirm}
        title={
          confirm.mode === "bulk" ? "Delete selected items?" : "Delete item?"
        }
        message={
          confirm.mode === "bulk"
            ? confirm.payload?.hasOldItems
              ? `${confirm.payload?.oldItemsCount || 0} selected item(s) were not created today. Deleting them may cause report mismatch. Are you sure you want to continue?`
              : `Are you sure you want to delete ${confirm.payload?.count || 0} selected item(s)?`
            : confirm.payload?.sameDay === false
              ? `This item was not created today. Deleting it may cause report mismatch. Are you sure you want to delete ${confirm.payload?.label || ""}?`
              : `Are you sure you want to delete ${confirm.payload?.label || ""}?`
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={deletingOne || deletingMany}
        destructive
      />
    </div>
  );
};

export default LabelledStockList;
